import { z } from 'zod';

import {
    optionalTrimmed,
    smsAudience,
    type V3CommsProposal,
    type V3CommsReceipt,
} from '@apps/web/features/phoebe_chat_v3/model/comms_types';

const sendSmsOutputSchema = z.object({
    status: z.enum(['sent', 'partial', 'needs_confirmation', 'blocked']),
    results: z.array(
        z.object({
            recipient_name: z.string().nullish(),
            status: z.enum(['sent', 'needs_confirmation', 'blocked', 'failed']),
            warnings: z.array(z.string()),
        }),
    ),
    notes: z.array(z.string()),
});

const placeVoiceCallOutputSchema = z.object({
    status: z.enum(['calling', 'needs_confirmation']),
    recipient_name: z.string().nullish(),
    warnings: z.array(z.string()),
    note: z.string(),
});

const approvePendingMessageOutputSchema = z.object({
    status: z.literal('approved'),
    message_id: z.string().nullish(),
    note: z.string(),
});

const rejectPendingMessageOutputSchema = z.object({
    status: z.literal('rejected'),
});

const editPendingMessageOutputSchema = z.object({
    status: z.literal('edited'),
});

// Mirrors the failure statuses the activity presentation treats as
// unsuccessful completions.
const FAILURE_STATUSES = new Set([
    'cancelled',
    'denied',
    'failed',
    'incomplete',
    'rejected',
]);

function firstWarning(
    results: ReadonlyArray<{ readonly warnings: ReadonlyArray<string> }>,
): string | null {
    for (const result of results) {
        const warning = optionalTrimmed(result.warnings[0]);
        if (warning !== null) {
            return warning;
        }
    }
    return null;
}

function deniedReceipt(proposal: V3CommsProposal): V3CommsReceipt {
    const chip = { label: 'Not approved', tone: 'muted' as const };
    switch (proposal.tool) {
        case 'send_sms':
            return {
                title: `Text not sent — ${smsAudience(proposal.recipients)}`,
                detail: null,
                chip,
            };
        case 'place_voice_call':
            return {
                title: `Call not placed — ${proposal.name}`,
                detail: null,
                chip,
            };
        case 'approve_pending_message':
            return { title: 'Reply left waiting', detail: null, chip };
        case 'edit_pending_message':
            return { title: 'Draft kept as written', detail: null, chip };
        case 'reject_pending_message':
            return { title: 'Reply kept', detail: null, chip };
        default: {
            const exhaustiveProposal: never = proposal;
            return exhaustiveProposal;
        }
    }
}

function failedReceipt(proposal: V3CommsProposal): V3CommsReceipt {
    const chip = { label: 'Failed', tone: 'destructive' as const };
    const detail = 'Phoebe could not complete this step.';
    switch (proposal.tool) {
        case 'send_sms':
            return {
                title: `Text not sent — ${smsAudience(proposal.recipients)}`,
                detail,
                chip,
            };
        case 'place_voice_call':
            return {
                title: `Call not placed — ${proposal.name}`,
                detail,
                chip,
            };
        case 'approve_pending_message':
            return { title: 'Reply not sent', detail, chip };
        case 'edit_pending_message':
            return { title: 'Draft unchanged', detail, chip };
        case 'reject_pending_message':
            return { title: 'Reply not discarded', detail, chip };
        default: {
            const exhaustiveProposal: never = proposal;
            return exhaustiveProposal;
        }
    }
}

// A completed run whose output does not match the tool's contract. Say only
// what is known: the step finished, without claiming a delivery outcome.
function finishedReceipt(proposal: V3CommsProposal): V3CommsReceipt {
    const chip = { label: 'Done', tone: 'muted' as const };
    switch (proposal.tool) {
        case 'send_sms':
            return {
                title: `Text — ${smsAudience(proposal.recipients)}`,
                detail: null,
                chip,
            };
        case 'place_voice_call':
            return { title: `Call — ${proposal.name}`, detail: null, chip };
        case 'approve_pending_message':
            return { title: 'Pending reply approval', detail: null, chip };
        case 'edit_pending_message':
            return { title: 'Draft rewrite', detail: null, chip };
        case 'reject_pending_message':
            return { title: 'Draft discard', detail: null, chip };
        default: {
            const exhaustiveProposal: never = proposal;
            return exhaustiveProposal;
        }
    }
}

function smsReceipt(
    proposal: Extract<V3CommsProposal, { readonly tool: 'send_sms' }>,
    output: unknown,
): V3CommsReceipt | null {
    const parsed = sendSmsOutputSchema.safeParse(output);
    if (!parsed.success) {
        return null;
    }
    const { status, results, notes } = parsed.data;
    const audience = smsAudience(proposal.recipients);
    const noteDetail =
        notes.length > 0 ? notes.join(' ') : firstWarning(results);
    switch (status) {
        case 'sent':
            return {
                title:
                    proposal.recipients.length === 1
                        ? `Text sent — ${audience}`
                        : `Texts sent — ${audience}`,
                detail: notes.length > 0 ? notes.join(' ') : null,
                chip: { label: 'Sent', tone: 'success' },
            };
        case 'partial': {
            const sentCount = results.filter(
                (result) => result.status === 'sent',
            ).length;
            return {
                title: `Texts sent to ${sentCount} of ${results.length} recipients`,
                detail: noteDetail,
                chip: { label: 'Partial', tone: 'warning' },
            };
        }
        case 'needs_confirmation':
            return {
                title: `Text held — ${audience}`,
                detail: noteDetail,
                chip: { label: 'Held', tone: 'warning' },
            };
        case 'blocked':
            return {
                title: `Text not sent — ${audience}`,
                detail: noteDetail,
                chip: { label: 'Blocked', tone: 'destructive' },
            };
        default: {
            const exhaustiveStatus: never = status;
            return exhaustiveStatus;
        }
    }
}

function voiceCallReceipt(
    proposal: Extract<V3CommsProposal, { readonly tool: 'place_voice_call' }>,
    output: unknown,
): V3CommsReceipt | null {
    const parsed = placeVoiceCallOutputSchema.safeParse(output);
    if (!parsed.success) {
        return null;
    }
    const name = optionalTrimmed(parsed.data.recipient_name) ?? proposal.name;
    if (parsed.data.status === 'calling') {
        return {
            title: `Call queued — ${name}`,
            detail: optionalTrimmed(parsed.data.note),
            chip: { label: 'Calling', tone: 'info' },
        };
    }
    const warnings = parsed.data.warnings
        .map((warning) => warning.trim())
        .filter((warning) => warning.length > 0);
    return {
        title: `Call held — ${name}`,
        detail:
            warnings.length > 0
                ? warnings.join(' ')
                : optionalTrimmed(parsed.data.note),
        chip: { label: 'Held', tone: 'warning' },
    };
}

function completedReceipt(
    proposal: V3CommsProposal,
    output: unknown,
): V3CommsReceipt | null {
    switch (proposal.tool) {
        case 'send_sms':
            return smsReceipt(proposal, output);
        case 'place_voice_call':
            return voiceCallReceipt(proposal, output);
        case 'approve_pending_message': {
            const parsed = approvePendingMessageOutputSchema.safeParse(output);
            if (!parsed.success) {
                return null;
            }
            const queued = optionalTrimmed(parsed.data.message_id) !== null;
            return {
                title: 'Reply approved',
                detail: optionalTrimmed(parsed.data.note),
                chip: queued
                    ? { label: 'Sent', tone: 'success' }
                    : { label: 'Not sent', tone: 'warning' },
            };
        }
        case 'edit_pending_message': {
            if (!editPendingMessageOutputSchema.safeParse(output).success) {
                return null;
            }
            return {
                title: 'Draft rewritten',
                detail: 'The reply is still waiting to be sent.',
                chip: { label: 'Edited', tone: 'info' },
            };
        }
        case 'reject_pending_message': {
            if (!rejectPendingMessageOutputSchema.safeParse(output).success) {
                return null;
            }
            return {
                title: 'Reply discarded',
                detail: proposal.reason,
                chip: { label: 'Discarded', tone: 'muted' },
            };
        }
        default: {
            const exhaustiveProposal: never = proposal;
            return exhaustiveProposal;
        }
    }
}

/**
 * Reduce a settled communication to its compact receipt. `denied` settles
 * without output (the tool never ran); a completed run prefers its parsed
 * output, then falls back to the entry's failure status or a bare "done".
 */
export function buildV3CommsReceipt({
    proposal,
    output,
    status,
    denied,
    hasError,
}: {
    readonly proposal: V3CommsProposal;
    readonly output: unknown;
    readonly status: string;
    readonly denied: boolean;
    readonly hasError: boolean;
}): V3CommsReceipt {
    if (denied) {
        return deniedReceipt(proposal);
    }
    if (hasError) {
        return failedReceipt(proposal);
    }
    return (
        completedReceipt(proposal, output) ??
        (FAILURE_STATUSES.has(status.toLowerCase())
            ? failedReceipt(proposal)
            : finishedReceipt(proposal))
    );
}
